import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useThemeColors } from '@/hooks/useThemeColors'
import { isTechnicianRole } from '@/lib/utils/roleUtils'

type UserItem = {
  id: number
  user_login?: string
  user_email?: string
  first_name?: string | null
  last_name?: string | null
  user_role?: string | { name?: string; [key: string]: unknown }
  user_role_name?: string
  role_name?: string
  role?: string
}

function userLabel(u: UserItem): string {
  const first = u.first_name ?? ''
  const last = u.last_name ?? ''
  const name = [first, last].filter(Boolean).join(' ').trim()
  return name || u.user_login || u.user_email || `User #${u.id}`
}

type Props = {
  isOpen: boolean
  onClose: () => void
  ticketId: number
  currentAssignedToId: number | null
  onSuccess: () => void
}

export default function ReassignModal({ isOpen, onClose, ticketId, currentAssignedToId, onSuccess }: Props) {
  const colors = useThemeColors()
  const [userSearchQuery, setUserSearchQuery] = useState('')
  const [userSearchResults, setUserSearchResults] = useState<UserItem[]>([])
  const [userSearching, setUserSearching] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [selectedUserDisplay, setSelectedUserDisplay] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) {
      setUserSearchQuery('')
      setUserSearchResults([])
      setSelectedUserId(null)
      setSelectedUserDisplay('')
      setError(null)
    }
  }, [isOpen])

  useEffect(() => {
    if (!userSearchQuery.trim() || userSearchQuery.trim().length < 2) {
      setUserSearchResults([])
      setUserSearching(false)
      return
    }
    setUserSearching(true)
    setUserSearchResults([])
    const t = setTimeout(async () => {
      try {
        const { data } = await apiClient.get('/user-management/users', {
          params: { search: userSearchQuery.trim(), per_page: 10, role_name: 'Technician' },
        })
        const list = data?.data ?? data
        const items = Array.isArray(list) ? list : list?.data ?? []
        setUserSearchResults(items.filter((user: UserItem) => isTechnicianRole(user)))
      } catch {
        setUserSearchResults([])
      } finally {
        setUserSearching(false)
      }
    }, 300)
    return () => clearTimeout(t)
  }, [userSearchQuery])

  const selectUser = (user: UserItem) => {
    setSelectedUserId(user.id)
    setSelectedUserDisplay(userLabel(user))
    setUserSearchQuery('')
    setUserSearchResults([])
    setError(null)
  }

  const clearSelection = () => {
    setSelectedUserId(null)
    setSelectedUserDisplay('')
    setError(null)
  }

  const handleSubmit = async () => {
    if (selectedUserId == null) {
      setError('Please select a user to assign.')
      return
    }
    if (selectedUserId === currentAssignedToId) {
      setError('Selected user is already the assignee.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await apiClient.post(`/ticket-management/all-tickets/${ticketId}/reassign`, {
        new_assignee_id: selectedUserId,
      })
      onSuccess()
      onClose()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      setError(msg ?? 'Failed to reassign ticket.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent bg={colors.modalBg}>
        <ModalHeader>Reassign ticket</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <FormControl>
              <FormLabel>New assignee</FormLabel>
              {selectedUserDisplay ? (
                <VStack align="stretch" spacing={2}>
                  <Text fontSize="sm" px={3} py={2} bg="gray.50" borderRadius="md">
                    {selectedUserDisplay}
                  </Text>
                  <Button size="sm" variant="outline" onClick={clearSelection}>
                    Change
                  </Button>
                </VStack>
              ) : (
                <>
                  <Input
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    placeholder="Search by login, email, name (min 2 chars)..."
                    autoComplete="off"
                  />
                  {userSearching && (
                    <HStack fontSize="sm" color="gray.500" mt={1} spacing={2}>
                      <Spinner size="sm" />
                      <Box as="span">Searching...</Box>
                    </HStack>
                  )}
                  {userSearchQuery.trim().length >= 2 && !userSearching && (
                    <VStack
                      align="stretch"
                      mt={2}
                      borderWidth="1px"
                      borderRadius="md"
                      maxH="200px"
                      overflowY="auto"
                      spacing={0}
                    >
                      {userSearchResults.length > 0 ? (
                        userSearchResults.map((user) => (
                          <Button
                            key={user.id}
                            size="sm"
                            variant="ghost"
                            justifyContent="flex-start"
                            onClick={() => selectUser(user)}
                            isDisabled={user.id === currentAssignedToId}
                          >
                            {userLabel(user)}
                            {user.id === currentAssignedToId ? ' (current)' : ''}
                          </Button>
                        ))
                      ) : (
                        <Text fontSize="sm" color="gray.500" px={3} py={2}>
                          No technicians available
                        </Text>
                      )}
                    </VStack>
                  )}
                </>
              )}
            </FormControl>
            {error && (
              <Box fontSize="sm" color="red.500" role="alert">
                {error}
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSubmit} isLoading={submitting} isDisabled={selectedUserId == null}>
            Reassign
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
